import React from 'react';
import { Grid, CircularProgress, Switch } from '@mui/material';
import { AccessTime as AccessTimeIcon } from '@mui/icons-material';

import {
  HoneyOpeningContainer,
  HoneyOpeningHeader,
  HoneyOpeningTitle,
  HoneyOpeningSubtitle,
  HoneyDaysContainer,
  HoneyDayRow,
  HoneyDayLabel,
  HoneyStatusSwitch,
  HoneyTimeField,
  HoneyTimeSeparator,
  HoneyStatusBadge,
  HoneySaveButton,
  HoneyActionsContainer,
  HoneyTimeInputsContainer,
  HoneyGridItem
} from './styled';

const daysOfWeek = [
  { day: 0, label: 'Monday' },
  { day: 1, label: 'Tuesday' },
  { day: 2, label: 'Wednesday' },
  { day: 3, label: 'Thursday' },
  { day: 4, label: 'Friday' },
  { day: 5, label: 'Saturday' },
  { day: 6, label: 'Sunday' },
];

const OpeningView = ({
  openingTimes,
  isLoading,
  isSaving,
  hasChanges,
  handleSwitchChange,
  handleTimeChange,
  handleSave
}) => {
  if (isLoading) {
    return (
      <HoneyOpeningContainer>
        <HoneyOpeningHeader>
          <CircularProgress size={40} sx={{ color: '#F1C40F' }} />
          <HoneyOpeningSubtitle sx={{ mt: 2 }}>
            Loading opening times...
          </HoneyOpeningSubtitle>
        </HoneyOpeningHeader>
      </HoneyOpeningContainer>
    );
  }

  return (
    <HoneyOpeningContainer id="venue-section-opening">
      <HoneyOpeningHeader>
        <HoneyOpeningTitle>
          <AccessTimeIcon fontSize="large" />
          Opening Times
        </HoneyOpeningTitle>
        <HoneyOpeningSubtitle>
          Set your venue's operating hours for each day of the week
        </HoneyOpeningSubtitle>
      </HoneyOpeningHeader>

      <HoneyDaysContainer>
        {openingTimes.length > 0 && daysOfWeek?.map(({ day, label }, index) => {
          const { time_start, time_end, status } = openingTimes[index] || {};
          const isOpen = status === 'AC';

          return (
            <HoneyDayRow 
              key={day} 
              container 
              spacing={2} 
              isOpen={isOpen}
              alignItems="center"
            >
              {/* Day Label */}
              <HoneyGridItem item xs={12} sm={3}>
                <HoneyDayLabel isOpen={isOpen}>
                  {label}
                </HoneyDayLabel>
              </HoneyGridItem>

              {/* Status Switch */}
              <HoneyGridItem item xs={12} sm={3}>
                <HoneyStatusSwitch
                  control={
                    <Switch
                      checked={isOpen}
                      onChange={() => handleSwitchChange(index)}
                      color="primary"
                    />
                  }
                  label={
                    <HoneyStatusBadge isOpen={isOpen}>
                      {isOpen ? 'Open' : 'Closed'}
                    </HoneyStatusBadge>
                  }
                />
              </HoneyGridItem>

              {/* Time Inputs */}
              <HoneyGridItem item xs={12} sm={6}>
                {isOpen && (
                  <HoneyTimeInputsContainer>
                    <HoneyTimeField
                      label="Opens"
                      type="time"
                      value={time_start || ''}
                      onChange={(e) => handleTimeChange(index, 'time_start', e.target.value)}
                      inputProps={{ step: 300 }} // 5 min intervals
                      size="small"
                    />
                    
                    <HoneyTimeSeparator>
                      to
                    </HoneyTimeSeparator>
                    
                    <HoneyTimeField
                      label="Closes"
                      type="time"
                      value={time_end || ''}
                      onChange={(e) => handleTimeChange(index, 'time_end', e.target.value)}
                      inputProps={{ step: 300 }} // 5 min intervals
                      size="small"
                    />
                  </HoneyTimeInputsContainer>
                )}
              </HoneyGridItem>
            </HoneyDayRow>
          );
        })}
      </HoneyDaysContainer>

      {/* Save Button */}
      {hasChanges && (
        <HoneyActionsContainer>
          <HoneySaveButton
            onClick={handleSave}
            disabled={isSaving}
            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <AccessTimeIcon />}
          >
            {isSaving ? 'Saving Changes...' : '🕐 Save Opening Times'}
          </HoneySaveButton>
        </HoneyActionsContainer>
      )}
    </HoneyOpeningContainer>
  );
};

export default OpeningView;